import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Upload, Trash2, Download, CreditCard, FileText, FolderArchive, Plus, User, Heart,
  AlertTriangle, CheckCircle, RefreshCw, Sparkles, ShieldAlert, Accessibility,
  Eye, EarOff, Utensils, Pill, Star, Activity, ShieldCheck, ShieldX, ChevronDown
} from 'lucide-react';
import { useConfirm } from '../components/ConfirmModal';
import { pdfToImage, isPdf, compressForGroq } from '../utils/pdfToImage';

const API = 'https://qbdp-backend-production.up.railway.app/api';

const CATEGORIES = [
  { key: 'identite',      label: "Pièce d'identité",     Icon: CreditCard,    color: '#0891b2', bg: '#ecfeff', ia: true  },
  { key: 'contrat',       label: 'Contrat / Réservation', Icon: FileText,      color: '#16a34a', bg: '#f0fdf4', ia: false },
  { key: 'medical',       label: 'Médical & Accessibilité', Icon: Heart,       color: '#dc2626', bg: '#fef2f2', ia: true  },
  { key: 'administratif', label: 'Administratif',         Icon: FolderArchive, color: '#d97706', bg: '#fffbeb', ia: false },
];

const ACCEPT = '.pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.doc,.docx,.xls,.xlsx,.odt,.ods';

const ALERTE_STYLE = {
  danger:  { bg: '#fef2f2', color: '#dc2626', border: '#fecaca', label: 'Alerte' },
  warning: { bg: '#fffbeb', color: '#d97706', border: '#fde68a', label: 'Attention' },
  ok:      { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0', label: 'Valide' },
  unknown: { bg: '#f9fafb', color: '#6b7280', border: '#e5e7eb', label: 'À vérifier' },
};

function fmtSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} Ko`;
  return `${(bytes / 1024 / 1024).toFixed(1)} Mo`;
}
function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function AlerteBadge({ alerte, resume }) {
  if (!alerte) return null;
  const s = ALERTE_STYLE[alerte] || ALERTE_STYLE.unknown;
  return (
    <div title={resume || ''} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 8,
      background: s.bg, color: s.color, border: `1px solid ${s.border}`, fontSize: 10, fontWeight: 700, cursor: 'help', flexShrink: 0 }}>
      {alerte === 'ok' ? <CheckCircle size={10} /> : <AlertTriangle size={10} />}
      {s.label}
    </div>
  );
}

function AuthenticiteBadge({ details }) {
  if (!details?.authenticite || details.authenticite === 'inconnu') return null;
  const suspect = details.authenticite === 'suspect';
  return (
    <div title={details.motif_suspect || ''} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 8,
      background: suspect ? '#fef2f2' : '#f0fdf4', color: suspect ? '#dc2626' : '#16a34a',
      border: `1px solid ${suspect ? '#fecaca' : '#bbf7d0'}`, fontSize: 10, fontWeight: 700, cursor: suspect ? 'help' : 'default', flexShrink: 0 }}>
      {suspect ? <ShieldX size={10} /> : <ShieldCheck size={10} />}
      {suspect ? 'Suspect' : 'Authentique'}
    </div>
  );
}

// Panel profil santé agrégé
function ProfilSante({ clientId, token, refreshTrigger }) {
  const [profil, setProfil] = useState(null);
  const [loading, setLoading] = useState(false);

  const charger = useCallback(() => {
    if (!clientId) return;
    setLoading(true);
    fetch(`${API}/documents-clients/profil-sante/${clientId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setProfil(d))
      .catch(() => setProfil(null))
      .finally(() => setLoading(false));
  }, [clientId, token]);

  useEffect(() => { charger(); }, [charger, refreshTrigger]);

  if (loading) return null;
  if (!profil || profil.nb_docs === 0) return null;

  const hasAllergiesGraves = profil.allergies?.some(a => a.anaphylaxie || a.gravite === 'grave');
  const hasMobilite = profil.mobilite && Object.values(profil.mobilite).some(v => v === true);
  const hasVision = profil.vision && Object.values(profil.vision).some(v => v === true);
  const hasAudition = profil.audition && Object.values(profil.audition).some(v => v === true);
  const hasRegimes = profil.regimes?.length > 0;
  const hasConditions = profil.conditions_medicales?.length > 0;
  const hasMeds = profil.medicaments_urgence?.length > 0;
  const hasBesoins = profil.besoins_speciaux?.length > 0;

  const aVerticalProfil = hasAllergiesGraves || hasMobilite || hasVision || hasAudition || hasRegimes || hasConditions || hasMeds || hasBesoins;
  if (!aVerticalProfil) return null;

  const borderColor = profil.alerte_max === 'danger' ? '#fecaca' : profil.alerte_max === 'warning' ? '#fde68a' : '#bbf7d0';
  const accentColor = profil.alerte_max === 'danger' ? '#dc2626' : profil.alerte_max === 'warning' ? '#d97706' : '#16a34a';

  return (
    <div style={{ background: '#fff', border: `1px solid ${borderColor}`, borderLeft: `4px solid ${accentColor}`, borderRadius: 14, padding: '16px 20px', marginBottom: 20 }}>
      <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Activity size={15} color={accentColor} /> Profil santé &amp; accessibilité
        {profil.alerte_max === 'danger' && (
          <span style={{ fontSize: 11, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, padding: '2px 8px', fontWeight: 700 }}>
            ATTENTION CRITIQUE
          </span>
        )}
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        {/* Allergies */}
        {profil.allergies?.length > 0 && (
          <div style={{ background: hasAllergiesGraves ? '#fef2f2' : '#fffbeb', border: `1px solid ${hasAllergiesGraves ? '#fecaca' : '#fde68a'}`, borderRadius: 10, padding: '8px 12px', minWidth: 160 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: hasAllergiesGraves ? '#dc2626' : '#d97706', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              <AlertTriangle size={12} /> Allergies {hasAllergiesGraves && '⚠️ ANAPHYLAXIE'}
            </div>
            {profil.allergies.map((a, i) => (
              <div key={i} style={{ fontSize: 11, color: '#374151', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: a.anaphylaxie ? '#dc2626' : a.gravite === 'grave' ? '#dc2626' : a.gravite === 'modéré' ? '#d97706' : '#6b7280', flexShrink: 0 }} />
                {a.nom} <span style={{ color: '#9ca3af', fontSize: 10 }}>({a.gravite})</span>
              </div>
            ))}
          </div>
        )}

        {/* Mobilité */}
        {hasMobilite && (
          <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '8px 12px', minWidth: 160 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#1a56db', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Accessibility size={12} /> Mobilité & Accessibilité
            </div>
            {profil.mobilite.pmr && <div style={{ fontSize: 11, color: '#374151', marginBottom: 2 }}>♿ PMR — chambre adaptée requise</div>}
            {profil.mobilite.fauteuil && <div style={{ fontSize: 11, color: '#374151', marginBottom: 2 }}>🦽 Fauteuil roulant</div>}
            {profil.mobilite.deambulateur && <div style={{ fontSize: 11, color: '#374151', marginBottom: 2 }}>🚶 Déambulateur</div>}
            {profil.mobilite.canne && <div style={{ fontSize: 11, color: '#374151', marginBottom: 2 }}>🦯 Canne / béquilles</div>}
            {profil.mobilite.escaliers_difficiles && <div style={{ fontSize: 11, color: '#374151', marginBottom: 2 }}>🚫 Escaliers impossibles — ascenseur obligatoire</div>}
            {profil.mobilite.chambre_rdc && <div style={{ fontSize: 11, color: '#374151', marginBottom: 2 }}>🏠 Chambre rez-de-chaussée obligatoire</div>}
            {profil.mobilite.details && <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4, fontStyle: 'italic' }}>{profil.mobilite.details}</div>}
          </div>
        )}

        {/* Vision */}
        {hasVision && (
          <div style={{ background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 10, padding: '8px 12px', minWidth: 140 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Eye size={12} /> Vision
            </div>
            {profil.vision.aveugle && <div style={{ fontSize: 11, color: '#374151', marginBottom: 2 }}>Non-voyant</div>}
            {profil.vision.malvoyant && !profil.vision.aveugle && <div style={{ fontSize: 11, color: '#374151', marginBottom: 2 }}>Malvoyant / basse vision</div>}
            {profil.vision.chien_guide && <div style={{ fontSize: 11, color: '#374151', marginBottom: 2 }}>🐕 Chien guide accepté</div>}
            {profil.vision.details && <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4, fontStyle: 'italic' }}>{profil.vision.details}</div>}
          </div>
        )}

        {/* Audition */}
        {hasAudition && (
          <div style={{ background: '#fdf4ff', border: '1px solid #f0abfc', borderRadius: 10, padding: '8px 12px', minWidth: 140 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#a21caf', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              <EarOff size={12} /> Audition
            </div>
            {profil.audition.sourd && <div style={{ fontSize: 11, color: '#374151', marginBottom: 2 }}>Sourd — communication écrite</div>}
            {profil.audition.malentendant && !profil.audition.sourd && <div style={{ fontSize: 11, color: '#374151', marginBottom: 2 }}>Malentendant</div>}
            {profil.audition.appareille && <div style={{ fontSize: 11, color: '#374151', marginBottom: 2 }}>Appareillé</div>}
            {profil.audition.signal_visuel && <div style={{ fontSize: 11, color: '#374151', marginBottom: 2 }}>⚡ Signaux visuels requis</div>}
            {profil.audition.details && <div style={{ fontSize: 10, color: '#6b7280', marginTop: 4, fontStyle: 'italic' }}>{profil.audition.details}</div>}
          </div>
        )}

        {/* Régimes */}
        {hasRegimes && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '8px 12px', minWidth: 140 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Utensils size={12} /> Régimes alimentaires
            </div>
            {profil.regimes.map((r, i) => (
              <div key={i} style={{ fontSize: 11, color: '#374151', marginBottom: 2 }}>• {r}</div>
            ))}
          </div>
        )}

        {/* Médicaments urgence */}
        {hasMeds && (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '8px 12px', minWidth: 140 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#dc2626', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Pill size={12} /> Médicaments urgence
            </div>
            {profil.medicaments_urgence.map((m, i) => (
              <div key={i} style={{ fontSize: 11, color: '#374151', marginBottom: 2 }}>• {m}</div>
            ))}
          </div>
        )}

        {/* Conditions médicales */}
        {hasConditions && (
          <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 10, padding: '8px 12px', minWidth: 140 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#ea580c', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Activity size={12} /> Conditions médicales
            </div>
            {profil.conditions_medicales.map((c, i) => (
              <div key={i} style={{ fontSize: 11, color: '#374151', marginBottom: 2 }}>• {c}</div>
            ))}
          </div>
        )}

        {/* Besoins spéciaux */}
        {hasBesoins && (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '8px 12px', minWidth: 140 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Star size={12} /> Besoins spéciaux
            </div>
            {profil.besoins_speciaux.map((b, i) => (
              <div key={i} style={{ fontSize: 11, color: '#374151', marginBottom: 2 }}>• {b}</div>
            ))}
          </div>
        )}
      </div>

      {/* Recommandations */}
      {(profil.recommandations_chambre?.length > 0 || profil.recommandations_repas?.length > 0 || profil.recommandations_communication?.length > 0) && (
        <div style={{ borderTop: '1px solid #f3f4f6', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {profil.recommandations_chambre?.map((r, i) => r && (
            <div key={i} style={{ fontSize: 11, color: '#374151', display: 'flex', gap: 6 }}>
              <span style={{ fontSize: 13 }}>🛏️</span> <strong>Chambre :</strong> {r}
            </div>
          ))}
          {profil.recommandations_repas?.map((r, i) => r && (
            <div key={i} style={{ fontSize: 11, color: '#374151', display: 'flex', gap: 6 }}>
              <span style={{ fontSize: 13 }}>🍽️</span> <strong>Repas :</strong> {r}
            </div>
          ))}
          {profil.recommandations_communication?.map((r, i) => r && (
            <div key={i} style={{ fontSize: 11, color: '#374151', display: 'flex', gap: 6 }}>
              <span style={{ fontSize: 13 }}>💬</span> <strong>Communication :</strong> {r}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DocumentsClients() {
  const [clients, setClients]               = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [docs, setDocs]                     = useState([]);
  const [uploading, setUploading]           = useState(null);
  const [analysing, setAnalysing]           = useState(new Set());
  const [toast, setToast]                   = useState(null);
  const [profilRefresh, setProfilRefresh]   = useState(0);
  const { showConfirm, confirmModal }       = useConfirm();
  const [clientSearch, setClientSearch]     = useState('');
  const [clientSelectorOpen, setClientSelectorOpen] = useState(false);
  const clientSelectorRef                   = useRef(null);
  const token = sessionStorage.getItem('qbdp_token');

  useEffect(() => {
    if (!clientSelectorOpen) return;
    const handler = (e) => {
      if (clientSelectorRef.current && !clientSelectorRef.current.contains(e.target))
        setClientSelectorOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [clientSelectorOpen]);

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    fetch(`${API}/clients`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => {
        const list = (Array.isArray(d) ? d : (d.clients || []))
          .sort((a, b) => (a.nom || '').localeCompare(b.nom || '', 'fr', { sensitivity: 'base' }));
        setClients(list);
        if (list.length > 0) setSelectedClient(list[0]);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedClient) return;
    setDocs([]);
    fetch(`${API}/documents-clients?client_id=${selectedClient.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setDocs(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [selectedClient]);

  const fetchDoc = async (docId) => {
    const r = await fetch(`${API}/documents-clients?client_id=${selectedClient.id}`, { headers: { Authorization: `Bearer ${token}` } });
    const d = await r.json();
    return (Array.isArray(d) ? d : []).find(x => x.id === docId) || null;
  };

  const rechargerDoc = async (docId) => {
    const updated = await fetchDoc(docId).catch(() => null);
    if (updated) {
      setDocs(prev => prev.map(p => p.id === docId ? updated : p));
      setProfilRefresh(n => n + 1);
    }
  };

  const analyserDoc = async (docId) => {
    setAnalysing(prev => new Set([...prev, docId]));
    try {
      for (let i = 0; i < 10; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const updated = await fetchDoc(docId).catch(() => null);
        if (updated) {
          setDocs(prev => prev.map(p => p.id === docId ? updated : p));
          if (updated.ai_alerte !== null) break;
        }
      }
      setProfilRefresh(n => n + 1);
    } finally {
      setAnalysing(prev => { const s = new Set(prev); s.delete(docId); return s; });
    }
  };

  const relancerAnalyse = async (docId) => {
    setAnalysing(prev => new Set([...prev, docId]));
    try {
      await fetch(`${API}/documents-clients/${docId}/analyser`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      rechargerDoc(docId);
      showToast('Analyse relancée');
    } catch { showToast('Erreur analyse', false); }
    finally { setAnalysing(prev => { const s = new Set(prev); s.delete(docId); return s; }); }
  };

  const handleUpload = async (categorie, file, iaActive) => {
    if (!file || !selectedClient) return;
    if (file.size > 15 * 1024 * 1024) { showToast('Fichier trop lourd (max 15 Mo)', false); return; }
    setUploading(categorie);
    try {
      let nom = file.name;
      let mime = file.type;
      let data;

      if (isPdf(file)) {
        showToast('Conversion PDF en cours…');
        const converted = await pdfToImage(file);
        data = converted.data;
        mime = converted.mime;
        nom = converted.nom;
      } else {
        data = await new Promise((res, rej) => {
          const reader = new FileReader();
          reader.onload = () => res(reader.result);
          reader.onerror = rej;
          reader.readAsDataURL(file);
        });
      }

      // Compression si image trop grande pour Groq (limite 4MB base64)
      if (mime && mime.startsWith('image/')) {
        data = await compressForGroq(data);
        mime = 'image/jpeg';
      }

      const r = await fetch(`${API}/documents-clients`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ client_id: selectedClient.id, categorie, nom, mime, data }),
      });
      const d = await r.json();
      if (d.id) {
        setDocs(prev => [d, ...prev]);
        if (iaActive) {
          showToast('Document ajouté — analyse IA en cours…');
          analyserDoc(d.id);
        } else {
          showToast('Document ajouté');
        }
      } else showToast(d.error || 'Erreur', false);
    } catch (err) { showToast(err.message || 'Erreur réseau', false); }
    setUploading(null);
  };

  const handleDelete = async (id, nom) => {
    const ok = await showConfirm({
      title: 'SUPPRIMER CE DOCUMENT',
      subtitle: nom,
      body: 'Cette action est irréversible. Le document sera définitivement supprimé.',
      confirmLabel: 'Supprimer',
      confirmColor: '#dc2626',
      icon: Trash2,
    });
    if (!ok) return;
    await fetch(`${API}/documents-clients/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    setDocs(prev => prev.filter(d => d.id !== id));
    setProfilRefresh(n => n + 1);
    showToast('Document supprimé');
  };

  const handleDownload = async (doc) => {
    try {
      const r = await fetch(`${API}/documents-clients/${doc.id}/data`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      const a = document.createElement('a'); a.href = d.data; a.download = d.nom; a.click();
    } catch { showToast('Erreur téléchargement', false); }
  };

  const initiales = (c) => {
    if (!c?.nom) return '?';
    return c.nom.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  const alertesGlobales = docs.filter(d => d.ai_alerte === 'danger' || d.ai_alerte === 'warning')
    .sort((a, b) => (a.ai_alerte === 'danger' ? 0 : 1) - (b.ai_alerte === 'danger' ? 0 : 1));

  return (
    <div style={{ padding: 32, maxWidth: 1060, margin: '0 auto' }}>

      {toast && (
        <div style={{ position: 'fixed', top: 24, right: 24, zIndex: 2000, padding: '12px 20px', borderRadius: 12,
          background: toast.ok ? '#f0fdf4' : '#fef2f2', color: toast.ok ? '#16a34a' : '#dc2626',
          border: `1px solid ${toast.ok ? '#bbf7d0' : '#fecaca'}`, fontWeight: 600, fontSize: 13,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          {toast.msg}
        </div>
      )}

      {/* Panel alertes critiques */}
      {alertesGlobales.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #fecaca', borderRadius: 14, padding: '16px 20px', marginBottom: 16, borderLeft: '4px solid #dc2626' }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#dc2626', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            <ShieldAlert size={14} /> {alertesGlobales.length} alerte{alertesGlobales.length > 1 ? 's' : ''} IA — action requise
          </div>
          {alertesGlobales.map(d => {
            const cat = CATEGORIES.find(c => c.key === d.categorie);
            const s = ALERTE_STYLE[d.ai_alerte];
            const details = d.ai_details;
            return (
              <div key={d.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: '1px solid #fef2f2' }}>
                <AlerteBadge alerte={d.ai_alerte} />
                {details?.authenticite === 'suspect' && <AuthenticiteBadge details={details} />}
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{cat?.label} — <span style={{ color: '#6b7280', fontWeight: 400 }}>{d.nom}</span></div>
                  {d.ai_resume && <div style={{ fontSize: 11, color: s?.color || '#6b7280', marginTop: 2 }}>{d.ai_resume}</div>}
                  {details?.motif_suspect && <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>⚠️ {details.motif_suspect}</div>}
                  {d.date_expiration && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 1 }}>Expiration : {fmtDate(d.date_expiration)}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Sélecteur client — dropdown searchable */}
      <div ref={clientSelectorRef} style={{ position: 'relative', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, marginBottom: 20 }}>
        <button
          onClick={() => { setClientSelectorOpen(o => !o); setClientSearch(''); }}
          style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px',
            background: 'none', border: 'none', cursor: 'pointer', borderRadius: 14 }}>
          <User size={13} color="#374151" />
          {selectedClient ? (
            <>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--blue)', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                {initiales(selectedClient)}
              </div>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e', flex: 1, textAlign: 'left' }}>{selectedClient.nom}</span>
            </>
          ) : (
            <span style={{ fontSize: 13, color: '#9ca3af', flex: 1, textAlign: 'left' }}>
              {clients.length === 0 ? 'Aucun client enregistré' : 'Sélectionner un client'}
            </span>
          )}
          <span style={{ fontSize: 11, color: '#9ca3af', background: '#f3f4f6', borderRadius: 20, padding: '2px 8px', flexShrink: 0 }}>{clients.length}</span>
          <ChevronDown size={14} color="#9ca3af" style={{ transform: clientSelectorOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
        </button>
        {clientSelectorOpen && clients.length > 0 && (
          <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, zIndex: 200,
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden' }}>
            <div style={{ padding: '8px 12px', borderBottom: '1px solid #f3f4f6' }}>
              <input
                autoFocus
                value={clientSearch}
                onChange={e => setClientSearch(e.target.value)}
                placeholder="Rechercher…"
                style={{ width: '100%', border: 'none', outline: 'none', fontSize: 13, color: '#374151', background: 'transparent' }}
              />
            </div>
            <div style={{ maxHeight: 240, overflowY: 'auto' }}>
              {clients
                .filter(c => !clientSearch || c.nom.toLowerCase().includes(clientSearch.toLowerCase()))
                .map(c => {
                  const active = selectedClient?.id === c.id;
                  return (
                    <button key={c.id} onClick={() => { setSelectedClient(c); setClientSelectorOpen(false); }}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
                        background: active ? '#eff6ff' : 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
                      <div style={{ width: 28, height: 28, borderRadius: '50%', background: active ? 'var(--blue)' : '#e5e7eb',
                        color: active ? '#fff' : '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0 }}>
                        {initiales(c)}
                      </div>
                      <span style={{ fontSize: 13, fontWeight: active ? 700 : 400, color: active ? 'var(--blue)' : '#374151', flex: 1 }}>{c.nom}</span>
                    </button>
                  );
                })}
              {clients.filter(c => !clientSearch || c.nom.toLowerCase().includes(clientSearch.toLowerCase())).length === 0 && (
                <div style={{ padding: '12px 16px', fontSize: 13, color: '#9ca3af', textAlign: 'center' }}>Aucun résultat</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Panel profil santé */}
      {selectedClient && (
        <ProfilSante clientId={selectedClient.id} token={token} refreshTrigger={profilRefresh} />
      )}

      {/* Grille 4 catégories */}
      {selectedClient && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
          {CATEGORIES.map(({ key, label, Icon, color, bg, ia }) => {
            const catDocs = docs.filter(d => d.categorie === key);
            const fileRef = React.createRef();
            return (
              <div key={key} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, overflow: 'hidden' }}>
                <div style={{ background: bg, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e5e7eb' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 10, background: color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon size={18} color="white" />
                    </div>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {label}
                        {ia && <span title="Analyse IA active" style={{ fontSize: 9, background: '#fef3c7', color: '#d97706', border: '1px solid #fde68a', borderRadius: 4, padding: '1px 5px', fontWeight: 600 }}>IA</span>}
                      </div>
                      <div style={{ fontSize: 11, color: '#6b7280' }}>{catDocs.length} document{catDocs.length !== 1 ? 's' : ''}</div>
                    </div>
                  </div>
                  <button onClick={() => fileRef.current?.click()} disabled={uploading === key}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', borderRadius: 8,
                      border: 'none', background: color, color: '#fff', fontSize: 12, fontWeight: 600,
                      cursor: uploading === key ? 'default' : 'pointer', opacity: uploading === key ? 0.6 : 1 }}>
                    {uploading === key ? <><Upload size={13} /> Envoi...</> : <><Plus size={13} /> Ajouter</>}
                  </button>
                  <input ref={fileRef} type="file" accept={ACCEPT} style={{ display: 'none' }}
                    onChange={e => { handleUpload(key, e.target.files[0], ia); e.target.value = ''; }} />
                </div>

                <div style={{ padding: catDocs.length ? 0 : 20 }}>
                  {catDocs.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 12, padding: '24px 0' }}>Aucun document</div>
                  ) : (
                    catDocs.map((doc, i) => {
                      const details = doc.ai_details;
                      return (
                        <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
                          borderBottom: i < catDocs.length - 1 ? '1px solid #f3f4f6' : 'none', background: '#fff',
                          borderLeft: details?.authenticite === 'suspect' ? '3px solid #dc2626' : 'none' }}>
                          <FileText size={15} color={color} style={{ flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 500, color: '#1a1a2e', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {doc.nom}
                            </div>
                            <div style={{ fontSize: 10, color: '#9ca3af' }}>
                              {fmtDate(doc.created_at)}{doc.taille ? ` · ${fmtSize(doc.taille)}` : ''}
                              {doc.date_expiration && <span style={{ color: doc.ai_alerte === 'danger' ? '#dc2626' : '#d97706', marginLeft: 6 }}>Exp. {fmtDate(doc.date_expiration)}</span>}
                            </div>
                            {doc.ai_resume && (
                              <div style={{ fontSize: 10, color: ALERTE_STYLE[doc.ai_alerte]?.color || '#6b7280', marginTop: 2, fontStyle: 'italic' }}>
                                {doc.ai_resume}
                              </div>
                            )}
                            {details?.motif_suspect && (
                              <div style={{ fontSize: 10, color: '#dc2626', marginTop: 2, fontWeight: 600 }}>
                                ⚠️ DOCUMENT SUSPECT : {details.motif_suspect}
                              </div>
                            )}
                          </div>
                          {analysing.has(doc.id) ? (
                            <div title="Analyse IA en cours…" style={{ padding: '5px 6px', display: 'flex', alignItems: 'center', opacity: 0.6 }}>
                              <RefreshCw size={12} color="#d97706" style={{ animation: 'spin 1s linear infinite' }} />
                            </div>
                          ) : (
                            <>
                              <AlerteBadge alerte={doc.ai_alerte} resume={doc.ai_resume} />
                              {details?.authenticite && <AuthenticiteBadge details={details} />}
                            </>
                          )}
                          {ia && (
                            <button onClick={() => relancerAnalyse(doc.id)} title="Relancer l'analyse IA"
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '3px', opacity: 0.5 }}>
                              <Sparkles size={11} color="#d97706" />
                            </button>
                          )}
                          <button onClick={() => handleDownload(doc)} title="Télécharger"
                            style={{ background: '#f3f4f6', border: 'none', borderRadius: 7, padding: '5px 7px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                            <Download size={12} color="#374151" />
                          </button>
                          <button onClick={() => handleDelete(doc.id, doc.nom)} title="Supprimer"
                            style={{ background: '#fef2f2', border: 'none', borderRadius: 7, padding: '5px 7px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                            <Trash2 size={12} color="#dc2626" />
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
      {confirmModal}
    </div>
  );
}
